import { ExpandableSection, Flex, LinkAnchor, Typo } from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { generatePath, useNavigate } from 'react-router'

import { Quote } from '../../../../domain'
import { useExpandableSectionConfig } from '../../../../hooks/adjustments/expandableSection'
import { useQuoteData } from '../../../../hooks/adjustments/quote'
import {
  tDefaults,
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { useAdjustments } from '../../../../state'
import { QuoteManagementConfig } from '../../../../state/configuration'
import { FormFormatter } from '../../../formBuilder'
import { ProposalPath } from '../../proposal/ProposalPath'
import { RenewalPath } from '../RenewalPath'

export interface PolicyManagementSectionProps {
  config: QuoteManagementConfig
  quote: Quote
}

const tKey = TKey('components.policy-management')

export const QuoteManagement: FunctionComponent<PolicyManagementSectionProps> = ({
  quote,
  config,
}) => {
  const navigate = useNavigate()

  const { t } = useWriskTranslation()

  const { getAdjustment } = useAdjustments()

  const onUpdateClick = useCallback(
    (e) => {
      e.stopPropagation()
      navigate(
        [
          ProposalPath.DISCLOSURE,
          generatePath(RenewalPath.SECTION, { sectionId: config.id }),
        ].join('/'),
      )
    },
    [config, navigate],
  )

  const toData = useQuoteData(quote)

  const [header, inputs] = useExpandableSectionConfig({
    sectionId: config.id,
    form: config.form,
    tKey,
    toData,
  })

  const expandedComponents = inputs.map((input) => {
    const adjustment = getAdjustment(input.name)

    return (
      <Flex key={input.name} px={4} py={3} flexDirection='column' alignItems='flex-start'>
        <Typo typoSize='xs' color='bodySecondary' mb={1}>
          {t(tDefaults('product.headers', input.name))}
        </Typo>
        <Typo as='pre'>
          <FormFormatter input={input} value={toData(adjustment)} t={t} />
        </Typo>
      </Flex>
    )
  })

  return (
    <ExpandableSection
      p={2}
      variant='raised'
      header={
        <Flex>
          <Typo fontWeight='bold'>{header}</Typo>
          <Flex>
            <LinkAnchor
              variant='standalone'
              position='relative'
              zIndex={1000}
              typoSize='sm'
              onClick={onUpdateClick}
              data-testid={`button-${config.id}`}
            >
              {t([tKey('actions.update'), tDefaults('actions.update')])}
            </LinkAnchor>
          </Flex>
        </Flex>
      }
    >
      {expandedComponents}
    </ExpandableSection>
  )
}
